import React from "react";
import { CircularProgress, DialogContentText, Typography, Box, Stack, DialogTitle, IconButton } from "@mui/material";
import { FilePlus, X } from "react-feather";
import ModalLayout from "./Layout";
import ModalLayoutHeader from "./Layout/Header";
import ModalLayoutContent from "./Layout/Content";
import ModalLayoutFooter from "./Layout/Footer";
import ModalLayoutBtn from "./Layout/Btn";
import ModalLayoutInput from "./Layout/Input";

/**
 *
 * @param {{
 *  isShown: boolean;
 *  onHide: () => void;
 *  onAction: () => void;
 *  activeBranch: string;
 *  createFileLog: string;
 *  filename: string;
 *  newFilename: () => void;
 *  creating: boolean;
 * }} data
 */
const CreateFileModal = ({
  isShown,
  onHide,
  onAction,
  activeBranch,
  createFileLog,
  filename,
  newFilename,
  creating,
}) => {
  return (
    <ModalLayout 
      isShown={isShown} 
      onHide={onHide}
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "12px",
          boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
          overflow: "hidden",
          background: "linear-gradient(to bottom, #ffffff, #f8f9fa)",
          border: "1px solid rgba(0,0,0,0.08)",
          transition: "all 0.3s ease-in-out"
        }
      }}
    >
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        position={"relative"}
        padding={"16px 24px"}
        borderBottom={"1px solid rgba(0,0,0,0.05)"}
        sx={{ background: "rgba(235,245,255,0.6)" }}
      >
        <DialogTitle
          padding={"0 !important"}
          sx={{
            color: "#3f51b5",
            fontWeight: 800,
            fontSize: "11px",
            display: "flex",
            alignItems: "center",
            "::before": {
              content: '""',
              display: "inline-block",
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: "#3f51b5",
              marginRight: "10px",
              animation: "pulse 1.5s infinite",
              "@keyframes pulse": {
                "0%": { boxShadow: "0 0 0 0 rgba(63, 81, 181, 0.7)" },
                "70%": { boxShadow: "0 0 0 10px rgba(63, 81, 181, 0)" },
                "100%": { boxShadow: "0 0 0 0 rgba(63, 81, 181, 0)" }
              }
            }
          }}
        >
          Create a new file in branch &nbsp;
          <Typography
            component={"span"}
            position={"relative"}
            fontSize={"11px"}
            paddingX={1}
            paddingY={"1px"}
            fontWeight={600}
            lineHeight={1.5}
            color="#fff"
            sx={{ 
              background: "linear-gradient(45deg, #5c6bc0, #7986cb)",
              borderRadius: "50px",
              padding: "2px 10px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
            }}
          >
            {activeBranch}
          </Typography>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onHide}
          sx={{
            position: "absolute",
            right: 16,
            top: 10,
            color: "#495057",
            "&:hover": {
              background: "rgba(0,0,0,0.05)",
              transform: "rotate(90deg)",
              transition: "transform 0.3s ease"
            }
          }}
        >
          <X size={16} />
        </IconButton>
      </Stack>
      <ModalLayoutContent sx={{ padding: "24px 28px" }}>
        <Box sx={{ 
          display: "flex", 
          flexDirection: "column", 
          alignItems: "center",
          gap: "20px"
        }}>
          <img 
            src="/images/newfile.jpg" 
            width="15%" 
            alt="" 
            style={{ 
              borderRadius: "8px", 
              boxShadow: "0 4px 8px rgba(0,0,0,0.05)" 
            }} 
          />
          <ModalLayoutInput
            type="text"
            aria-label="Text input with dropdown button"
            placeholder="Your new file"
            onChange={newFilename}
            sx={{
              fontSize: "11px",
              borderRadius: "8px",
              border: "1px solid rgba(0,0,0,0.1)",
              padding: "10px 16px",
              width: "100%",
              transition: "all 0.2s",
              "&:focus": {
                borderColor: "#7986cb",
                boxShadow: "0 0 0 3px rgba(121,134,203,0.15)"
              }
            }}
          />
          {createFileLog && (
            <DialogContentText
              color="#d32f2f"
              fontWeight={700}
              fontSize="11px"
              textAlign={"center"}
              sx={{
                background: "rgba(248,215,218,0.5)",
                padding: "10px 12px",
                borderRadius: "6px",
                border: "1px dashed rgba(220,53,69,0.3)",
                animation: "fadeIn 1.1s ease-in",
                "@keyframes fadeIn": {
                  "0%": { opacity: 0, transform: "translateY(-5px)" },
                  "50%": { opacity: 0.5, transform: "translateY(-5px)" },
                  "100%": { opacity: 1, transform: "translateY(0)" }
                }
                }}
            >
              {createFileLog}
            </DialogContentText>
          )}
          {filename === "models/" && (
            <Typography
              fontSize="11px"
              color="#6c757d"
              textAlign="center"
              sx={{
                background: "rgba(241,250,255,0.8)",
                padding: "8px 12px",
                borderRadius: "6px",
                border: "1px solid rgba(0,123,255,0.1)"
              }}
            >
              Your SQL files have to be stored in a subfolder inside the models folder.
            </Typography>
          )}
        </Box>
      </ModalLayoutContent>
      <ModalLayoutFooter sx={{ padding: "16px 24px" }}>
        <ModalLayoutBtn 
          color="success" 
          disabled={creating} 
          onClick={onAction}
          variant="contained"
          sx={{
            fontSize: "11px",
            borderRadius: "8px",
            fontWeight: 600,
            textTransform: "none",
            padding: "8px 20px",
            transition: "all 0.2s",
            background: "linear-gradient(45deg, #2e7d32, #43a047)",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(46,125,50,0.3)"
            },
            "&:disabled": {
              background: "#b0bec5",
              color: "#ffffff"
            }
          }}
        >
          <FilePlus size={16} style={{ marginRight: "6px" }} />
          {creating ? (
            <>
              Creating File...&nbsp;&nbsp;
              <CircularProgress size={16} color="inherit" />
            </>
          ) : (
            "Create File"
          )}
        </ModalLayoutBtn>
      </ModalLayoutFooter>
    </ModalLayout>
  );
};

export default CreateFileModal;
